import React, { useState, useEffect } from 'react';
import { InvestmentSheet } from '../../types/InvestmentSheet';
import { investmentSheetService } from '../../services/investmentSheetService';
import { crvProductService, phProductService, craProductService } from '../../services/productService';
import { crvPhaseService, phPhaseService, craPhaseService } from '../../services/phaseService';
import { formatCurrency } from '../../utils/format';
import './InvestorInvestments.css';

interface InvestorInvestmentsProps {
  investorId: string;
}

interface ProductInfo {
  id: string;
  nombre: string;
}

interface PhaseInfo {
  id: string;
  nombre: string;
}

const InvestorInvestments: React.FC<InvestorInvestmentsProps> = ({ investorId }) => {
  const [investments, setInvestments] = useState<InvestmentSheet[]>([]);
  const [products, setProducts] = useState<Map<string, ProductInfo>>(new Map());
  const [phases, setPhases] = useState<Map<string, PhaseInfo>>(new Map());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [investorId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load investments
      const investmentsResponse = await investmentSheetService.getByInvestor(investorId);
      setInvestments(investmentsResponse.data);

      // Load products
      const [crvProducts, phProducts, craProducts] = await Promise.all([
        crvProductService.getAll(),
        phProductService.getAll(),
        craProductService.getAll(),
      ]);

      const productsMap = new Map<string, ProductInfo>();
      [...crvProducts.data, ...phProducts.data, ...craProducts.data].forEach(p => {
        productsMap.set(p.id!, { id: p.id!, nombre: p.nombreProducto });
      });
      setProducts(productsMap);

      // Load phases dynamically based on investments
      const phasesMap = new Map<string, PhaseInfo>();
      const phasePromises: Promise<void>[] = [];

      investmentsResponse.data.forEach(investment => {
        if (!phasesMap.has(investment.phaseId)) {
          const loadPhase = async () => {
            try {
              let phase;
              if (investment.sheetType === 'CRV') {
                const response = await crvPhaseService.getById(investment.productId, investment.phaseId);
                phase = response.data;
              } else if (investment.sheetType === 'PH') {
                const response = await phPhaseService.getById(investment.productId, investment.phaseId);
                phase = response.data;
              } else if (investment.sheetType === 'CRA') {
                const response = await craPhaseService.getById(investment.productId, investment.phaseId);
                phase = response.data;
              }
              if (phase) {
                phasesMap.set(phase.id!, { id: phase.id!, nombre: phase.nombreFase });
              }
            } catch (error) {
              console.error(`Error loading phase ${investment.phaseId}:`, error);
            }
          };
          phasePromises.push(loadPhase());
        }
      });

      await Promise.all(phasePromises);
      setPhases(phasesMap);
    } catch (error) {
      console.error('Error loading investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (value: number | undefined) => {
    if (value === undefined || value === null) return '-';
    return `${value.toFixed(2)}%`;
  };

  const getInteresesDevengados = (sheet: InvestmentSheet): number => {
    if (sheet.sheetType === 'CRV') {
      return (sheet as any).totalIntDevengados || 0;
    } else if (sheet.sheetType === 'PH') {
      return (sheet as any).interesesAcumulados || 0;
    }
    return 0;
  };

  const getInteresesLiquidados = (sheet: InvestmentSheet): number => {
    if (sheet.sheetType === 'CRV') {
      return (sheet as any).interesesAnticipados || 0;
    } else if (sheet.sheetType === 'PH') {
      return (sheet as any).interesesPagados || 0;
    } else if (sheet.sheetType === 'CRA') {
      return (sheet as any).repartosCobrados || 0;
    }
    return 0;
  };

  const getPendienteTotal = (sheet: InvestmentSheet): number => {
    return (sheet as any).liqFinalPendiente || 0;
  };

  const getRtoTotal = (sheet: InvestmentSheet): number => {
    const interesesDev = getInteresesDevengados(sheet);
    if (!sheet.importe || sheet.importe === 0) return 0;
    return (interesesDev / sheet.importe) * 100;
  };

  const getRtoAnualizado = (sheet: InvestmentSheet): number => {
    return (sheet as any).rtoAnualActual || 0;
  };

  const getProjectPhase = (sheet: InvestmentSheet): string => {
    const product = products.get(sheet.productId);
    const phase = phases.get(sheet.phaseId);

    if (!product || !phase) return '-';
    return `${product.nombre} / ${phase.nombre}`;
  };

  const formatEstado = (estado: string | undefined): string => {
    if (!estado) return '-';
    // Reemplazar underscores por espacios y aplicar casos especiales
    return estado
      .replace('EN_EXPANSION', 'EN EXPANSIÓN')
      .replace(/_/g, ' ');
  };

  if (loading) {
    return <div className="loading">Cargando inversiones...</div>;
  }

  if (investments.length === 0) {
    return (
      <div className="investor-investments">
        <div className="investments-header">
          <h3>Registro de Inversiones</h3>
        </div>
        <div className="empty-investments">
          <p>No hay inversiones registradas para este inversor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="investor-investments">
      <div className="investments-header">
        <h3>Registro de Inversiones</h3>
      </div>

      <div className="investments-table-container">
        <table className="investments-table">
          <thead>
            <tr>
              <th>ID Inversión</th>
              <th>Proyecto / Fase</th>
              <th>Importe</th>
              <th>Int. Devengados</th>
              <th>Int. Liquidados</th>
              <th>Pendiente (Total)</th>
              <th>Rto. Total</th>
              <th>Rto. Anualizado</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {investments.map((investment) => (
              <tr key={investment.id}>
                <td className="investment-id">{investment.id?.substring(0, 8).toUpperCase()}</td>
                <td>{getProjectPhase(investment)}</td>
                <td className="amount">{formatCurrency(investment.importe)}</td>
                <td className="amount">{formatCurrency(getInteresesDevengados(investment))}</td>
                <td className="amount">{formatCurrency(getInteresesLiquidados(investment))}</td>
                <td className="amount">{formatCurrency(getPendienteTotal(investment))}</td>
                <td className="percentage">{formatPercentage(getRtoTotal(investment))}</td>
                <td className="percentage">{formatPercentage(getRtoAnualizado(investment))}</td>
                <td>
                  <span className={`investment-status status-${investment.estado?.toLowerCase().replace(/\./g, '').replace(/\s/g, '-').replace(/_/g, '-')}`}>
                    {formatEstado(investment.estado)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvestorInvestments;
